import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from 'next-localization';

import { flatten, isEmpty, pickBy } from 'lodash-es';

import { cn } from '@/lib/utils';
import { NeuFeedback } from '@hcahealthcaredpx/neutron-react';
import { HighlightText } from '@xsf/shared-lib/components/highlight-text';

import styles from '../landing-search.module.css';
import { AutocompleteCategories, AutocompleteItems } from '../landing-search.types';

interface Props {
  isLoading: boolean;
  items?: AutocompleteItems;
  searchRef: React.RefObject<HTMLInputElement>;
  handleItemClick: (item: unknown, category: string) => void;
  handleItemMouseDown: (item: unknown) => void;
}

const translationMapping = {
  [AutocompleteCategories.providers]: 'OAS.LandingPage.Providers',
  [AutocompleteCategories.locations]: 'OAS.LandingPage.Locations',
  [AutocompleteCategories.specialties]: 'OAS.LandingPage.Specialties',
  [AutocompleteCategories.visibleTerms]: 'OAS.LandingPage.ProceduresAndConditions',
};

const Loading = () => {
  return (
    <div className="w-100 d-flex justify-content-center overflow-hidden">
      <NeuFeedback kind="spinner" size="small" color="blue" />
    </div>
  );
};

const DropdownContent = ({
  isLoading,
  items,
  searchRef,
  handleItemClick,
  handleItemMouseDown,
}: Props): JSX.Element | null => {
  const { t } = useI18n();
  const filtered = useMemo(() => {
    return pickBy(items, (x) => !isEmpty(x)) as AutocompleteItems;
  }, [items]);

  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const buttonRefHandler = useCallback((el: HTMLButtonElement | null, currentIndex: number) => {
    buttonRefs.current[currentIndex] = el;
  }, []);

  //  Needs to know what is the length of all items so when we reach the bottom to go to the first and vice versa
  const flattenItemsLength = useMemo(() => {
    return flatten(Object.values(items || {}))?.length;
  }, [items]);

  useEffect(() => {
    if (focusedIndex !== null && focusedIndex >= 0) {
      buttonRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);

  if (isLoading) {
    return <Loading />;
  }

  if (!items) {
    return null;
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => ((prev || 0) + 1) % flattenItemsLength);
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => ((prev || 0) <= 0 ? flattenItemsLength - 1 : (prev || 0) - 1));
    }
  };

  let globalIndex = 0;
  return (
    <div onKeyDown={handleKeyDown}>
      {Object.entries(filtered).map(([category, items], idx) => (
        <div key={`${category}-${+idx}`}>
          <div
            // Todo: revise styles
            className={cn(
              'neu-text--cerulean fw-medium position-sticky top-0 neu-background--white z-3 neu-shadow',
              styles.neuSearchHeaderWrapper
            )}
          >
            <div role="presentation" className={styles.neuSearchHeader}>
              <span className={styles.listCategory}>
                {t(translationMapping[category as AutocompleteCategories])}
              </span>
            </div>
          </div>
          <ul className={styles.ulListWrapper}>
            {items.map((item, index) => {
              const currentIndex = globalIndex++;

              return (
                <li
                  key={`${item}-${+index}`}
                  className={styles.liItemWrapper}
                  aria-label={item.value}
                >
                  <button
                    type="button"
                    onMouseDown={() => handleItemMouseDown(item)}
                    onClick={() => handleItemClick(item, category)}
                    className={styles.neuButton}
                    ref={(el) => buttonRefHandler(el, currentIndex)}
                  >
                    <HighlightText
                      text={item.value}
                      highlight={searchRef.current?.value || ''}
                      className={styles.highlighted}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default DropdownContent;
